import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@project/contexts/AuthContext";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { toast } from "sonner";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  Upload,
  FileText,
  Calendar,
  Globe,
  Github,
  CheckCircle2,
  ArrowRight,
  File,
  X,
  Plus,
  Trash2,
  Target,
  Loader2,
} from "lucide-react";

const PARSE_RESUME_URL = "https://atsai.universal-software.in/parse-resume";

/** Shape returned by ATS parse-resume API */
interface ParsedResumePayload {
  full_name?: string;
  contact_info?: {
    email?: string;
    phone?: string;
  };
  location?: string;
  summary?: string;
  skills?: string[];
  eligible_roles?: string[];
  experience?: Array<{
    job_title?: string;
    company?: string;
    duration?: string;
    responsibilities?: string | string[];
  }>;
  education?: Array<{
    degree?: string;
    institution?: string;
    year?: string;
  }>;
}

function normalizeResponsibilities(raw: string | string[] | undefined): string {
  if (raw == null) {
    return "";
  }
  if (Array.isArray(raw)) {
    return raw.map(line => `• ${String(line).trim()}`).join("\n");
  }
  return raw;
}

function unwrapParsedJson(data: unknown): ParsedResumePayload | null {
  if (!data || typeof data !== "object") {
    return null;
  }
  const o = data as Record<string, unknown>;
  const inner =
    (o.data as ParsedResumePayload | undefined) ??
    (o.candidate as ParsedResumePayload | undefined) ??
    (o.result as ParsedResumePayload | undefined);
  if (inner && typeof inner === "object") {
    return inner as ParsedResumePayload;
  }
  return data as ParsedResumePayload;
}
import hireflowLogo from "@project/assets/hireflow-logo.png";

// Types for experience and education
interface ExperienceItem {
  job_title: string;
  company: string;
  duration: string;
  responsibilities: string;
}

interface EducationItem {
  degree: string;
  institution: string;
  year: string;
}

export default function CandidateProfileSetup() {
  const { user, completeProfile } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    full_name: user?.name || "",
    contact_info: {
      email: user?.email || "",
      phone: "",
    },
    location: "",
    summary: "",
    skills: [] as string[],
    eligible_roles: [] as string[],
  });

  const [skillInput, setSkillInput] = useState("");
  const [roleInput, setRoleInput] = useState("");
  const [experience, setExperience] = useState<ExperienceItem[]>([
    { job_title: "", company: "", duration: "", responsibilities: "" },
  ]);
  const [education, setEducation] = useState<EducationItem[]>([
    { degree: "", institution: "", year: "" },
  ]);

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [parsingResume, setParsingResume] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateContactInfo = (field: keyof typeof formData.contact_info, value: string) => {
    setFormData(prev => ({
      ...prev,
      contact_info: { ...prev.contact_info, [field]: value },
    }));
  };

  const addSkill = () => {
    if (skillInput.trim()) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()],
      }));
      setSkillInput("");
    }
  };

  const removeSkill = (index: number) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index),
    }));
  };

  const addRole = () => {
    if (roleInput.trim()) {
      setFormData(prev => ({
        ...prev,
        eligible_roles: [...prev.eligible_roles, roleInput.trim()],
      }));
      setRoleInput("");
    }
  };

  const removeRole = (index: number) => {
    setFormData(prev => ({
      ...prev,
      eligible_roles: prev.eligible_roles.filter((_, i) => i !== index),
    }));
  };

  const addExperience = () => {
    setExperience([
      ...experience,
      { job_title: "", company: "", duration: "", responsibilities: "" },
    ]);
  };

  const updateExperience = (
    index: number,
    field: keyof ExperienceItem,
    value: string
  ) => {
    const updated = [...experience];
    updated[index][field] = value;
    setExperience(updated);
  };

  const removeExperience = (index: number) => {
    setExperience(experience.filter((_, i) => i !== index));
  };

  const addEducation = () => {
    setEducation([...education, { degree: "", institution: "", year: "" }]);
  };

  const updateEducation = (
    index: number,
    field: keyof EducationItem,
    value: string
  ) => {
    const updated = [...education];
    updated[index][field] = value;
    setEducation(updated);
  };

  const removeEducation = (index: number) => {
    setEducation(education.filter((_, i) => i !== index));
  };

  const applyParsedResume = (parsed: ParsedResumePayload) => {
    setFormData(prev => {
      const loc = parsed.location?.trim();
      const useLocation =
        loc && loc.toUpperCase() !== "N/A" ? loc : prev.location;

      return {
        ...prev,
        full_name: parsed.full_name?.trim() || prev.full_name,
        contact_info: {
          email:
            parsed.contact_info?.email?.trim() || prev.contact_info.email,
          phone:
            parsed.contact_info?.phone?.trim() || prev.contact_info.phone,
        },
        location: useLocation,
        summary: parsed.summary?.trim() || prev.summary,
        skills:
          parsed.skills && parsed.skills.length > 0
            ? parsed.skills
            : prev.skills,
        eligible_roles:
          parsed.eligible_roles && parsed.eligible_roles.length > 0
            ? parsed.eligible_roles
            : prev.eligible_roles,
      };
    });

    if (parsed.experience && parsed.experience.length > 0) {
      setExperience(
        parsed.experience.map(exp => ({
          job_title: exp.job_title?.trim() ?? "",
          company: exp.company?.trim() ?? "",
          duration: exp.duration?.trim() ?? "",
          responsibilities: normalizeResponsibilities(exp.responsibilities),
        }))
      );
    }

    if (parsed.education && parsed.education.length > 0) {
      setEducation(
        parsed.education.map(edu => ({
          degree: edu.degree?.trim() ?? "",
          institution: edu.institution?.trim() ?? "",
          year: edu.year?.trim() ?? "",
        }))
      );
    }
  };

  const handleResumeSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowed.includes(file.type)) {
      toast.error("Only PDF and DOCX files are allowed");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be under 5MB");
      e.target.value = "";
      return;
    }

    setResumeFile(file);
    setParsingResume(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const res = await fetch(PARSE_RESUME_URL, {
        method: "POST",
        body: formDataUpload,
      });

      const text = await res.text();
      let raw: unknown;
      try {
        raw = text ? JSON.parse(text) : null;
      } catch {
        throw new Error("Resume service returned invalid JSON");
      }

      if (!res.ok) {
        const msg =
          typeof raw === "object" &&
          raw !== null &&
          "error" in raw &&
          (raw as { error: unknown }).error != null
            ? String((raw as { error: unknown }).error)
            : text || res.statusText;
        throw new Error(msg);
      }

      const payload = unwrapParsedJson(raw);
      if (!payload || typeof payload !== "object") {
        throw new Error("Could not read parsed resume data");
      }

      applyParsedResume(payload);
      toast.success("Resume uploaded — fields filled from your CV. Please review.");
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to parse resume";
      toast.error(msg);
    } finally {
      setParsingResume(false);
    }
  };

  const handleSubmit = async () => {
    if (
      !formData.full_name.trim() ||
      !formData.contact_info.email.trim() ||
      !formData.contact_info.phone.trim()
    ) {
      toast.error("Please fill all required fields (Name, Email, Phone)");
      return;
    }
    if (!resumeFile) {
      toast.error("Please upload your resume");
      return;
    }

    setSubmitting(true);
    // Simulate API call with complete profile data
    const profileData = {
      ...formData,
      experience,
      education,
      resume: resumeFile.name,
    };
    console.log("Profile Data:", profileData);
    
    await new Promise(r => setTimeout(r, 1500));
    completeProfile();
    setSubmitting(false);
    toast.success("Profile completed successfully! Welcome to HireFlow.");
    navigate("/candidate/dashboard");
  };

  const RequiredStar = () => <span className="ml-0.5 text-destructive">*</span>;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-border/60 bg-card/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <img
              src={hireflowLogo}
              alt="HireFlow"
              className="h-8 w-auto rounded-lg object-contain"
            />
            <div>
              <h1 className="font-display text-sm font-bold text-foreground">
                HireFlow
              </h1>
              <p className="text-[9px] text-muted-foreground">AI-Powered ATS</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
              <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
            </div>
            Step 2 of 2 — Complete Profile
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-6 pb-20">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="space-y-6"
        >
          {/* Welcome */}
          <motion.div variants={item} className="space-y-2 py-4 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
              <User className="h-7 w-7 text-primary" />
            </div>
            <h2 className="font-display text-xl font-bold text-foreground">
              Complete Your Profile
            </h2>
            <p className="mx-auto max-w-md text-sm text-muted-foreground">
              Fill in your details so recruiters can find and evaluate your
              profile. This information will be visible to hiring managers.
            </p>
          </motion.div>

          {/* Resume Upload */}
          <motion.div variants={item} className="section-card space-y-3 p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <FileText className="h-4 w-4 text-primary" /> Upload Resume{" "}
              <RequiredStar />
            </h3>
            {!resumeFile ? (
              <div
                className={`rounded-xl border-2 border-dashed border-border/60 p-8 text-center transition-all ${parsingResume ? "pointer-events-none opacity-60" : "cursor-pointer hover:border-primary/50 hover:bg-primary/5"}`}
                onClick={() =>
                  !parsingResume && fileInputRef.current?.click()
                }
              >
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <Upload className="h-5 w-5 text-primary" />
                </div>
                <p className="text-sm font-medium text-foreground">
                  Click to upload your Resume
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  PDF or DOCX • Max 5MB
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  onChange={handleResumeSelect}
                />
              </div>
            ) : (
              <div className="flex items-center gap-4 rounded-xl border border-border/40 bg-muted/30 p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  {parsingResume ? (
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  ) : (
                    <File className="h-5 w-5 text-primary" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {resumeFile.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {parsingResume
                      ? "Reading your resume…"
                      : `${(resumeFile.size / 1024).toFixed(1)} KB`}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-destructive hover:text-destructive"
                  disabled={parsingResume}
                  onClick={() => {
                    setResumeFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                >
                  <X className="mr-1 h-3.5 w-3.5" /> Remove
                </Button>
              </div>
            )}
          </motion.div>

          {/* Personal Details */}
          <motion.div variants={item} className="section-card space-y-4 p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <User className="h-4 w-4 text-primary" /> Contact Information
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label className="text-[12px] font-medium">
                  Full Name <RequiredStar />
                </Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Your full name"
                    value={formData.full_name}
                    onChange={e => updateField("full_name", e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[12px] font-medium">
                  Email <RequiredStar />
                </Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="your@email.com"
                    value={formData.contact_info.email}
                    onChange={e => updateContactInfo("email", e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[12px] font-medium">
                  Phone Number <RequiredStar />
                </Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="+91 9876543210"
                    value={formData.contact_info.phone}
                    onChange={e => updateContactInfo("phone", e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[12px] font-medium">Location</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="City, State, Country"
                    value={formData.location}
                    onChange={e => updateField("location", e.target.value)}
                    className="h-9 pl-9 text-sm"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Professional Summary */}
          <motion.div variants={item} className="section-card space-y-3 p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <FileText className="h-4 w-4 text-primary" /> Professional Summary
            </h3>
            <Textarea
              placeholder="Brief summary about your professional background, expertise, and career goals..."
              value={formData.summary}
              onChange={e => updateField("summary", e.target.value)}
              className="min-h-[100px] resize-none text-sm"
            />
          </motion.div>

          {/* Skills */}
          <motion.div variants={item} className="section-card space-y-4 p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Briefcase className="h-4 w-4 text-primary" /> Skills
            </h3>
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Add a skill (e.g., Python, React, SQL)"
                  value={skillInput}
                  onChange={e => setSkillInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSkill();
                    }
                  }}
                  className="h-9 text-sm"
                />
                <Button
                  size="sm"
                  onClick={addSkill}
                  className="h-9 shrink-0"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {formData.skills.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.skills.map((skill, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-1.5 rounded-lg border border-border/50 bg-muted/50 px-3 py-1.5 text-sm"
                    >
                      <span>{skill}</span>
                      <button
                        onClick={() => removeSkill(index)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Eligible Roles */}
          <motion.div variants={item} className="section-card space-y-4 p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
              <Target className="h-4 w-4 text-primary" /> Eligible/Preferred Roles
            </h3>
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Add a role (e.g., AI ML Engineer, Data Scientist)"
                  value={roleInput}
                  onChange={e => setRoleInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addRole();
                    }
                  }}
                  className="h-9 text-sm"
                />
                <Button
                  size="sm"
                  onClick={addRole}
                  className="h-9 shrink-0"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {formData.eligible_roles.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.eligible_roles.map((role, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary"
                    >
                      <span>{role}</span>
                      <button
                        onClick={() => removeRole(index)}
                        className="text-primary/70 hover:text-destructive"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          {/* Professional Details */}
          <motion.div variants={item} className="section-card space-y-4 p-5">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
                <Briefcase className="h-4 w-4 text-primary" /> Work Experience
              </h3>
              <Button
                size="sm"
                variant="outline"
                onClick={addExperience}
                className="h-8 text-xs"
              >
                <Plus className="mr-1 h-3.5 w-3.5" /> Add More
              </Button>
            </div>
            <div className="space-y-4">
              {experience.map((exp, index) => (
                <div
                  key={index}
                  className="space-y-3 rounded-lg border border-border/50 bg-muted/30 p-4"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-muted-foreground">
                      Experience {index + 1}
                    </p>
                    {experience.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeExperience(index)}
                        className="h-7 text-xs text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <Label className="text-[12px] font-medium">
                        Job Title
                      </Label>
                      <Input
                        placeholder="e.g., AI/ML Engineer"
                        value={exp.job_title}
                        onChange={e =>
                          updateExperience(index, "job_title", e.target.value)
                        }
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-[12px] font-medium">Company</Label>
                      <Input
                        placeholder="e.g., Universal Software"
                        value={exp.company}
                        onChange={e =>
                          updateExperience(index, "company", e.target.value)
                        }
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-[12px] font-medium">
                        Duration
                      </Label>
                      <Input
                        placeholder="e.g., Feb 2024 - Present"
                        value={exp.duration}
                        onChange={e =>
                          updateExperience(index, "duration", e.target.value)
                        }
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-[12px] font-medium">
                        Key Responsibilities
                      </Label>
                      <Textarea
                        placeholder="Describe your key responsibilities and achievements..."
                        value={exp.responsibilities}
                        onChange={e =>
                          updateExperience(
                            index,
                            "responsibilities",
                            e.target.value
                          )
                        }
                        className="mt-1 min-h-[70px] resize-none text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Education */}
          <motion.div variants={item} className="section-card space-y-4 p-5">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
                <GraduationCap className="h-4 w-4 text-primary" /> Education
              </h3>
              <Button
                size="sm"
                variant="outline"
                onClick={addEducation}
                className="h-8 text-xs"
              >
                <Plus className="mr-1 h-3.5 w-3.5" /> Add More
              </Button>
            </div>
            <div className="space-y-4">
              {education.map((edu, index) => (
                <div
                  key={index}
                  className="space-y-3 rounded-lg border border-border/50 bg-muted/30 p-4"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-muted-foreground">
                      Education {index + 1}
                    </p>
                    {education.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeEducation(index)}
                        className="h-7 text-xs text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Label className="text-[12px] font-medium">
                        Degree / Qualification
                      </Label>
                      <Input
                        placeholder="e.g., B.Tech in Computer Science Engineering"
                        value={edu.degree}
                        onChange={e =>
                          updateEducation(index, "degree", e.target.value)
                        }
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-[12px] font-medium">
                        Institution / University
                      </Label>
                      <Input
                        placeholder="e.g., Institute of Advanced Research"
                        value={edu.institution}
                        onChange={e =>
                          updateEducation(index, "institution", e.target.value)
                        }
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-[12px] font-medium">
                        Graduation Year
                      </Label>
                      <Input
                        placeholder="e.g., 2025"
                        value={edu.year}
                        onChange={e =>
                          updateEducation(index, "year", e.target.value)
                        }
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Submit */}
          <motion.div variants={item}>
            <Button
              className="btn-glow h-11 w-full gap-2 text-sm font-semibold"
              onClick={handleSubmit}
              disabled={submitting || parsingResume}
            >
              {submitting ? (
                "Saving Profile..."
              ) : (
                <>
                  Complete Profile & Continue <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
            <p className="mt-2 text-center text-[11px] text-muted-foreground">
              You can update your profile anytime from Settings
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
